import { definedMap, dontAwait } from "@glideapps/ts-necessities";
import { asNumber, asString, loadedDefinedMap, loadedDefinedMapWithDefault } from "common/components/component-helpers";
import type { Icon } from "common/components/icons";
import { evalSummary, makeDropImageHandler } from "common/components/portable-renderers";
import { Action, ActionSource, asUI, LOADING } from "common/components/types";
import type { GlideFunctionalRenderer } from "common/components/uis";
import type { ListItemAccessoryPosition } from "common/generator/array-screens/list-item-flags";
import { ListItemFlags } from "common/generator/array-screens/list-item-flags";
import { checkString } from "common/Support";
import { useEditComponentEnabledViaNetworkStatus } from "hooks/use-edit-components-enabled-via-network-status";
import { useForce } from "hooks/use-force";
import { getIsActionEnabled, useOnClickFromAction } from "hooks/use-on-click-from-action";
import { useForceValueProducers } from "hooks/use-value-producers";
import { hasFlag } from "webapp/lib/has-flag";
import { useChangeEmptyness, useListItemSizeChange } from "webapp/ui/smart/list/list-lib";
import ListItem from "../ui/smart/list-item/list-item";

export const AdaptiveListItemMaterialRenderer: GlideFunctionalRenderer = p => {
    // FIXME: subscribe to the first image
    const { env, getValueProducers, ctx } = p;
    const [force] = useForce();
    const [
        [
            titleValue,
            subtitleValue,
            captionValue,
            ,
            icon,
            fallbackInitials,
            onTap,
            onDropImage,
            actionSource,
            flags,
            accessoryView,
            accessoryPosition,
            itemID,
        ],
        [, subtitleVP, captionVP, imageVP],
    ] = useForceValueProducers(getValueProducers, force, ctx);
    const { title, subtitle, imageSource } = evalSummary(titleValue, subtitleValue, imageVP, force);
    const [editingAllowed, onLine] = useEditComponentEnabledViaNetworkStatus(env);

    const source = loadedDefinedMapWithDefault(
        actionSource,
        ActionSource.ListItem,
        v => checkString(v) as ActionSource
    );
    const { onClick, targetURL, autoRunInTabletMode } = useOnClickFromAction(onTap, source, ctx, env);

    let fallback: string | undefined;
    if (fallbackInitials === LOADING) {
        fallback = "";
    } else {
        fallback = definedMap(fallbackInitials, asString);
    }
    const caption = loadedDefinedMap(captionValue, asString);

    const iconName = loadedDefinedMap(icon, asString) as Icon | undefined;
    let flagsVal = loadedDefinedMap(flags, asNumber) as ListItemFlags;

    if (onClick === undefined) {
        flagsVal = flagsVal | ListItemFlags.Inert;
    }

    let accessory: React.ReactNode;
    if (accessoryView !== undefined && accessoryView !== null) {
        accessory = asUI(accessoryView).render(ctx, env);
    }
    const accessoryPositionValue = loadedDefinedMap(accessoryPosition, asString) as ListItemAccessoryPosition;

    if (imageVP !== undefined) {
        flagsVal = flagsVal | ListItemFlags.ReserveImageSpace;
    }

    if (subtitleVP !== undefined) {
        flagsVal = flagsVal | ListItemFlags.ReserveDetailSpace;
    }

    if (captionVP !== undefined) {
        flagsVal = flagsVal | ListItemFlags.ReserveCaptionSpace;
    }

    const itemIDVal = loadedDefinedMap(itemID, asString);

    const [isEnabled] = getIsActionEnabled(ctx, env, force, editingAllowed, onLine, onTap);

    let disableChevon = env.sizeClass === "tablet" && env.displayContext === "master";
    if (onTap instanceof Action) {
        if (!isEnabled) {
            disableChevon = true;
        }
    } else {
        disableChevon = true;
    }

    if (env.needsPush && autoRunInTabletMode && onClick !== undefined) {
        dontAwait(onClick(false));
    }

    const hasCaptionDep = useChangeEmptyness(caption);
    const textWraps = hasFlag(flagsVal, ListItemFlags.WrapText);
    const subtitleDep = textWraps ? subtitle : undefined;
    useListItemSizeChange(subtitleDep, hasCaptionDep);

    return (
        <ListItem
            title={titleValue === LOADING ? LOADING : title}
            subtitle={subtitleValue === LOADING ? LOADING : subtitle}
            caption={captionValue === LOADING ? LOADING : caption}
            imageSource={imageSource}
            fallbackInitials={fallback}
            flags={flagsVal}
            onClick={isEnabled ? onClick : undefined}
            targetUrl={targetURL}
            onDropImage={makeDropImageHandler(env, onDropImage, ActionSource.ListItem)}
            accessoryPosition={accessoryPositionValue}
            itemID={itemIDVal}
            appEnvironment={env.appEnvironment}
            menuItems={definedMap(iconName, i => [
                {
                    title: i,
                    icon: i,
                },
            ])}
            disableChevon={disableChevon}>
            {accessory}
        </ListItem>
    );
};
